const DEEPSEEK_API_KEY = import.meta.env.VITE_DEEPSEEK_API_KEY;
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

if (!DEEPSEEK_API_KEY) {
  console.error('DeepSeek API key is missing. Please set VITE_DEEPSEEK_API_KEY in your .env file');
}

interface DeepSeekResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

export const deepseekApi = {
  // Analyze squad application for quality scoring
  async analyzeApplication(applicationData: any) {
    if (!DEEPSEEK_API_KEY) {
      console.warn('DeepSeek API key missing, using mock response');
      return {
        motivation: 4,
        commitment: 4,
        experience: 3,
        teamFit: 4,
        overall: 3.75,
        feedback: "Strong application with good potential.",
        redFlags: [],
        strengths: ["Complete application", "Good team composition"]
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `You are an AI assistant that analyzes squad applications for a venture building program. 
              Score applications based on motivation, commitment, experience, and team fit. 
              Provide scores from 1-5 and brief reasoning. 
              Return your response as a JSON object with fields: 
              - motivation (number 1-5)
              - commitment (number 1-5)
              - experience (number 1-5)
              - teamFit (number 1-5)
              - overall (number 1-5)
              - feedback (string)
              - redFlags (array of strings, empty if none)
              - strengths (array of strings)`
            },
            {
              role: 'user',
              content: `Analyze this squad application:\n\n${JSON.stringify(applicationData, null, 2)}`
            }
          ],
          temperature: 0.7,
          max_tokens: 800
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error:', error);
      return {
        motivation: 3,
        commitment: 3,
        experience: 3,
        teamFit: 3,
        overall: 3,
        feedback: "Application received. Will be reviewed manually.",
        redFlags: [],
        strengths: ["Application complete"]
      };
    }
  },

  // Generate squad code and welcome message
  async generateSquadCode(squadName: string) {
    if (!DEEPSEEK_API_KEY) {
      return {
        squadCode: `FORGE-${squadName.substring(0, 3).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`,
        welcomeMessage: `Welcome to The Forge, ${squadName}! Your journey begins now.`,
        nextSteps: "• Complete squad onboarding\n• Schedule kickoff call\n• Access problem bank"
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `Generate a unique squad code and welcome message for a new squad joining The Forge program. 
              Squad code should be format: FORGE-XXX-123 (where XXX is first 3 letters of squad name).
              Make the welcome message inspiring and specific to the squad name.
              Return as JSON with fields: 
              - squadCode (string)
              - welcomeMessage (string)
              - nextSteps (string with 3 bullet points)`
            },
            {
              role: 'user',
              content: `Generate for squad: ${squadName}`
            }
          ],
          temperature: 0.8,
          max_tokens: 300
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error:', error);
      return {
        squadCode: `FORGE-${squadName.substring(0, 3).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`,
        welcomeMessage: `Welcome to The Forge, ${squadName}!`,
        nextSteps: "• Complete squad onboarding\n• Schedule kickoff call\n• Access problem bank"
      };
    }
  },

  // Analyze problem submission
  async analyzeProblemSubmission(problemData: any) {
    if (!DEEPSEEK_API_KEY) {
      return {
        clarity: 4,
        marketPotential: 3,
        feasibility: 3,
        recommendations: ["Define problem more clearly", "Add market size data"],
        estimatedValue: "Medium"
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `You are an AI that analyzes business problems submitted to a venture program. 
              Provide analysis including:
              - clarity (1-5)
              - marketPotential (1-5)
              - feasibility (1-5)
              - recommendations (array of strings)
              - estimatedValue (Low/Medium/High)
              Return as JSON.`
            },
            {
              role: 'user',
              content: `Analyze this business problem submission:\n\n${JSON.stringify(problemData, null, 2)}`
            }
          ],
          temperature: 0.7,
          max_tokens: 400
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error:', error);
      return {
        clarity: 3,
        marketPotential: 3,
        feasibility: 3,
        recommendations: ["Review needed"],
        estimatedValue: "To be determined"
      };
    }
  },

  // Generate interview questions based on application
  async generateInterviewQuestions(applicationData: any) {
    if (!DEEPSEEK_API_KEY) {
      return [
        "Tell us more about your team's experience.",
        "Why are you passionate about this problem?",
        "How will you commit 15 hours per week?"
      ];
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'Generate 5 personalized interview questions for a squad applying to The Forge program. Questions should be specific to their application responses. Return as JSON array of strings.'
            },
            {
              role: 'user',
              content: `Generate questions based on this application:\n\n${JSON.stringify(applicationData, null, 2)}`
            }
          ],
          temperature: 0.8,
          max_tokens: 300
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error:', error);
      return [];
    }
  },

  // Generate bootcamp daily content
  async generateBootcampContent(day: number, week: number, title: string, businessType: string, industry: string) {
    if (!DEEPSEEK_API_KEY) {
      console.warn('DeepSeek API key missing, using mock bootcamp content');
      return {
        id: `day-${day}-${Date.now()}`,
        day,
        week,
        title,
        learningObjectives: [
          `Understand ${title} fundamentals`,
          `Apply concepts to your ${businessType} business`,
          `Identify key metrics for success`,
          `Create actionable implementation plan`
        ],
        keyConcepts: [
          { term: 'Concept 1', definition: 'Definition 1', examples: ['Example 1', 'Example 2'] },
          { term: 'Concept 2', definition: 'Definition 2', examples: ['Example 1', 'Example 2'] },
          { term: 'Concept 3', definition: 'Definition 3', examples: ['Example 1', 'Example 2'] }
        ],
        tasks: [
          {
            title: `Task 1: ${businessType} Analysis`,
            description: `Analyze your ${businessType} business model and identify key areas for improvement.`,
            deliverables: ['Completed analysis document', 'Key findings summary'],
            estimatedTime: 2,
            resources: ['Template link', 'Example document']
          },
          {
            title: 'Task 2: Implementation Plan',
            description: 'Create a step-by-step plan to implement today\'s concepts.',
            deliverables: ['Action plan with timelines', 'Resource requirements'],
            estimatedTime: 1.5,
            resources: ['Planning template', 'Best practices guide']
          },
          {
            title: 'Task 3: Metrics Tracking',
            description: 'Define success metrics and set up tracking mechanisms.',
            deliverables: ['Metrics dashboard', 'Baseline measurements'],
            estimatedTime: 1,
            resources: ['Analytics guide', 'Metric definitions']
          }
        ],
        quiz: [
          {
            question: `What is the most important factor when considering ${title}?`,
            options: ['Option A', 'Option B', 'Option C', 'Option D'],
            correctAnswer: 0,
            explanation: 'This is the correct answer because...'
          },
          {
            question: 'How would you measure success for this topic?',
            options: ['Metric A', 'Metric B', 'Metric C', 'Metric D'],
            correctAnswer: 1,
            explanation: 'This metric is most relevant because...'
          }
        ],
        resources: [
          { type: 'video', title: 'Introduction Video', url: '#' },
          { type: 'article', title: 'Deep Dive Article', url: '#' },
          { type: 'template', title: 'Worksheet Template', url: '#' }
        ],
        aiGenerated: true,
        lastModified: new Date().toISOString()
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `You are an AI that generates educational content for a 28-day venture building bootcamp.
              Generate comprehensive daily content based on the day number, week, title, business type, and industry.
              
              Return a JSON object with the following structure:
              {
                "id": "day-{day}-{timestamp}",
                "day": number,
                "week": number,
                "title": string,
                "learningObjectives": array of 4 strings,
                "keyConcepts": array of objects with fields "term", "definition", and "examples" (array of strings),
                "tasks": array of objects with fields "title", "description", "deliverables" (array), "estimatedTime" (number in hours), "resources" (array),
                "quiz": array of objects with fields "question", "options" (array of 4 strings), "correctAnswer" (index 0-3), "explanation",
                "resources": array of objects with fields "type" (video/article/template/tool), "title", "url" (use "#" as placeholder),
                "aiGenerated": true,
                "lastModified": ISO string
              }
              
              Make content practical, actionable, and tailored to the business type and industry.
              Keep language clear and professional.`
            },
            {
              role: 'user',
              content: `Generate bootcamp content for:
              Day: ${day}
              Week: ${week}
              Title: ${title}
              Business Type: ${businessType}
              Industry: ${industry}`
            }
          ],
          temperature: 0.8,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      const content = JSON.parse(data.choices[0].message.content);
      
      // Ensure the content has the correct structure
      return {
        ...content,
        id: content.id || `day-${day}-${Date.now()}`,
        day,
        week,
        title,
        aiGenerated: true,
        lastModified: new Date().toISOString()
      };
    } catch (error) {
      console.error('DeepSeek API error generating bootcamp content:', error);
      
      // Return enhanced mock content as fallback
      const weekTopics: Record<number, string[]> = {
        1: ['Architecture', 'Load Testing', 'Performance', 'Bottlenecks', 'Security', 'Disaster Recovery', 'Review'],
        2: ['CAC', 'LTV', 'Margins', 'Break-Even', 'Pricing', 'Financial Modeling', 'Review'],
        3: ['Brand Identity', 'Positioning', 'Segmentation', 'Channels', 'Sales Strategy', 'Partnerships', 'Review'],
        4: ['Investor Personas', 'Pitch Deck', 'Financials', 'Due Diligence', 'Cap Table', 'Pitch Practice', 'Final Assessment']
      };
      
      const weekTopic = weekTopics[week]?.[(day % 7) - 1] || 'Core Concept';
      
      return {
        id: `day-${day}-${Date.now()}`,
        day,
        week,
        title,
        learningObjectives: [
          `Master ${weekTopic} for ${businessType} businesses`,
          `Apply ${weekTopic} principles to your ${industry} venture`,
          `Develop actionable ${weekTopic} strategy`,
          `Create deliverables for ${weekTopic} assessment`
        ],
        keyConcepts: [
          { 
            term: `${weekTopic} Fundamentals`, 
            definition: `Core principles of ${weekTopic} as applied to ${businessType} businesses in the ${industry} sector.`, 
            examples: ['Case study 1', 'Case study 2', 'Real-world application'] 
          },
          { 
            term: `Advanced ${weekTopic} Strategies`, 
            definition: `Sophisticated approaches to ${weekTopic} that drive competitive advantage.`, 
            examples: ['Strategy example A', 'Strategy example B'] 
          },
          { 
            term: `${weekTopic} Metrics & KPIs`, 
            definition: `Key performance indicators to measure success in ${weekTopic}.`, 
            examples: ['Metric 1: Definition', 'Metric 2: Definition', 'Metric 3: Definition'] 
          }
        ],
        tasks: [
          {
            title: `Task 1: ${weekTopic} Assessment`,
            description: `Evaluate your current ${weekTopic} approach and identify gaps.`,
            deliverables: ['Gap analysis document', 'Priority improvement areas'],
            estimatedTime: 2,
            resources: ['Assessment template', 'Industry benchmarks']
          },
          {
            title: `Task 2: ${weekTopic} Strategy Development`,
            description: `Create a comprehensive ${weekTopic} strategy for your venture.`,
            deliverables: ['Strategy document', 'Implementation roadmap'],
            estimatedTime: 2.5,
            resources: ['Strategy template', 'Best practices guide']
          },
          {
            title: `Task 3: ${weekTopic} Execution Plan`,
            description: `Develop a 30-day execution plan with clear milestones.`,
            deliverables: ['Execution timeline', 'Responsibility assignment'],
            estimatedTime: 1.5,
            resources: ['Project plan template', 'Resource estimation tool']
          }
        ],
        quiz: [
          {
            question: `What is the primary goal of ${weekTopic} in a ${businessType} business?`,
            options: [
              'Maximize short-term revenue',
              'Build sustainable competitive advantage',
              'Minimize operational costs',
              'Increase team size'
            ],
            correctAnswer: 1,
            explanation: `In ${businessType} businesses, ${weekTopic} focuses on building sustainable competitive advantage through systematic approach.`
          },
          {
            question: `Which metric is most important for measuring ${weekTopic} success?`,
            options: ['Metric A', 'Metric B', 'Metric C', 'Metric D'],
            correctAnswer: 1,
            explanation: `Metric B provides the most comprehensive view of ${weekTopic} performance.`
          }
        ],
        resources: [
          { type: 'video', title: `${weekTopic} Masterclass`, url: '#' },
          { type: 'article', title: `${weekTopic} Best Practices`, url: '#' },
          { type: 'template', title: `${weekTopic} Worksheet`, url: '#' },
          { type: 'tool', title: `${weekTopic} Calculator`, url: '#' }
        ],
        aiGenerated: true,
        lastModified: new Date().toISOString()
      };
    }
  },

  // Generate personalized feedback for founder after quiz
  async generateQuizFeedback(day: number, quizResults: any, founderProfile: any) {
    if (!DEEPSEEK_API_KEY) {
      return {
        score: 75,
        strengths: ['Good understanding of core concepts'],
        areasForImprovement: ['Could deepen application knowledge'],
        recommendations: ['Review case studies', 'Practice with real examples'],
        nextSteps: 'Continue with next day content'
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `You are an AI mentor providing personalized feedback to founders after they complete daily bootcamp quizzes.
              Analyze their performance and provide constructive feedback.
              Return as JSON with fields:
              - score (number 0-100)
              - strengths (array of strings)
              - areasForImprovement (array of strings)
              - recommendations (array of strings)
              - nextSteps (string)`
            },
            {
              role: 'user',
              content: `Generate feedback for:
              Day: ${day}
              Quiz Results: ${JSON.stringify(quizResults)}
              Founder Profile: ${JSON.stringify(founderProfile)}`
            }
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error generating quiz feedback:', error);
      return {
        score: 70,
        strengths: ['Attempted all questions'],
        areasForImprovement: ['Review incorrect answers'],
        recommendations: ['Study the key concepts again'],
        nextSteps: 'Proceed to next day'
      };
    }
  },

  // Generate personalized bootcamp summary and recommendations
  async generateBootcampSummary(founderProgress: any) {
    if (!DEEPSEEK_API_KEY) {
      return {
        overallPerformance: 'Good progress',
        keyStrengths: ['Consistent completion', 'Good quiz scores'],
        developmentAreas: ['Deeper application needed'],
        recommendations: ['Focus on practical implementation'],
        readinessScore: 75,
        readinessLevel: 'Ready for LOI assessment'
      };
    }

    try {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `Generate a comprehensive bootcamp summary for a founder who has completed the 28-day program.
              Include overall performance assessment, key strengths, areas for development, and readiness for LOI.
              Return as JSON with fields:
              - overallPerformance (string)
              - keyStrengths (array of strings)
              - developmentAreas (array of strings)
              - recommendations (array of strings)
              - readinessScore (number 0-100)
              - readinessLevel (string: "Not Ready", "Needs Improvement", "Ready for LOI", "Highly Recommended")`
            },
            {
              role: 'user',
              content: `Generate summary based on founder progress:\n\n${JSON.stringify(founderProgress, null, 2)}`
            }
          ],
          temperature: 0.7,
          max_tokens: 600
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      return JSON.parse(data.choices[0].message.content);
    } catch (error) {
      console.error('DeepSeek API error generating bootcamp summary:', error);
      return {
        overallPerformance: 'Satisfactory completion',
        keyStrengths: ['Completed all days'],
        developmentAreas: ['Could show deeper understanding'],
        recommendations: ['Review key concepts before LOI assessment'],
        readinessScore: 70,
        readinessLevel: 'Ready for LOI assessment'
      };
    }
  }
};